import { useEffect, useState } from 'react';

import useAccessBadge from '../hooks/useAccessBadge';

/**
 * Last value published by `useDetectBrowserTranslation`. Error boundaries
 * cannot call the hook, and a crash can happen before the effect that writes
 * the Faro session attribute has flushed.
 */
let publishedBrowserTranslation: string | null = null;

/**
 * Translation detected right now.
 *
 * Edge Translator stamps `_msttexthash` on the title. Chrome Translate adds
 * `translated-ltr` / `translated-rtl` on `<html>` and sets `lang` to the
 * target language. An in-app locale also sets `lang`, so lang alone is not
 * translation. Anything the hook has already decided (including Firefox /
 * Safari, which only show up as a lang mismatch against the preferred locale)
 * is used when the DOM has no translator marker.
 */
export function getDetectedBrowserTranslation(): string | null {
  const fromDom = readBrowserTranslationFromDom();
  if (fromDom) return fromDom;
  return publishedBrowserTranslation;
}

function readBrowserTranslationFromDom(): string | null {
  if (typeof document === 'undefined') return null;

  const title = document.getElementsByTagName('title')[0];
  if (title?.hasAttribute('_msttexthash')) {
    return "idk; it's microsoft edge";
  }

  const html = document.documentElement;
  const chromeTranslated =
    !!html &&
    (html.classList.contains('translated-ltr') ||
      html.classList.contains('translated-rtl'));
  if (chromeTranslated && html.lang) return html.lang;

  return null;
}

export function useDetectBrowserTranslation(): {
  browserTranslation: "idk; it's microsoft edge" | string | null;
} {
  const chromeFirefoxSafariBrowserTranslation = useDetectChromeFirefoxSafariBrowserTranslation();
  const microsoftEdgeBrowserTranslation = useDetectMicrosoftEdgeBrowserTranslation();
  const browserTranslation =
    microsoftEdgeBrowserTranslation ?? chromeFirefoxSafariBrowserTranslation;

  useEffect(() => {
    publishedBrowserTranslation = browserTranslation;
  }, [browserTranslation]);

  return {
    browserTranslation,
  };
}

function useDetectMicrosoftEdgeBrowserTranslation() {
  const [browserTranslation, setBrowserTranslation] = useState<
    "idk; it's microsoft edge" | null
  >(null);
  useEffect(() => {
    const title = document.getElementsByTagName('title')[0];
    const detectTranslationMicrosoftEdge = () => {
      if (title?.hasAttribute('_msttexthash')) {
        setBrowserTranslation("idk; it's microsoft edge");
      } else {
        setBrowserTranslation(null);
      }
    };
    detectTranslationMicrosoftEdge();

    const observer = new MutationObserver(detectTranslationMicrosoftEdge);
    observer.observe(title, {
      attributes: true,
      attributeFilter: ['_msttexthash'],
      childList: false,
      characterData: false,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return browserTranslation;
}

function useDetectChromeFirefoxSafariBrowserTranslation() {
  const [browserTranslation, setBrowserTranslation] = useState<string | null>(null);
  const { accessBadge, isFetched } = useAccessBadge();
  const preferredLocale = accessBadge?.user.preferredLocale;
  const htmlLang = useHtmlLang();

  useEffect(() => {
    if (preferredLocale) {
      // if preferred locale is fetched, but doesn't match lang property, that means user is using browser translation
      if (htmlLang !== preferredLocale) {
        setBrowserTranslation(htmlLang);
      } else {
        setBrowserTranslation(null);
      }
    }
    if (isFetched && !preferredLocale) {
      // Compare base language codes (en vs es, fr, etc.) instead of full locale codes
      const htmlBaseLang = htmlLang?.split('-')[0];
      const isEnglishVariant = htmlBaseLang === 'en';

      if (!isEnglishVariant) {
        setBrowserTranslation(htmlLang);
      } else {
        setBrowserTranslation(null);
      }
    }
  }, [preferredLocale, isFetched, htmlLang]);

  return browserTranslation;
}

export function useHtmlLang() {
  const [htmlLang, setHtmlLang] = useState<string | null>(null);
  useEffect(() => {
    const detectLang = () => {
      const lang = document.documentElement.lang;
      if (lang !== htmlLang) {
        setHtmlLang(lang);
      }
    };
    // Initial check
    detectLang();

    // Observe changes to the html element's attributes
    const observer = new MutationObserver(detectLang);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
      childList: false,
      characterData: false,
    });

    return () => {
      observer.disconnect();
    };
  }, [htmlLang]);
  return htmlLang;
}
